// What the organization consumed, and what it pays every month for its channels and their hosts.
//
// A tag of its own on the backend, and a folder of its own here for the same reason: the money
// next door is a figure the organization holds or owes, and these are the **measurements** behind
// it. All three take `tenancy.billing.manage` — `usage_days` carries `amount_cents` on the same
// row as `quantity`, so there is no read that drops the money half to widen the audience.

import { api, withQuery } from '../client'
import type { components, operations } from '../schema'

export type UsageDayEntry = components['schemas']['UsageDayEntry']
export type UsageDayPage = components['schemas']['Page_UsageDayEntry']
export type UsagePeriodEntry = components['schemas']['UsagePeriodEntry']
export type RecurringChargeEntry = components['schemas']['RecurringChargeEntry']

/**
 * The days walk: `sku`, `from`, `to` (both `YYYY-MM-DD`, inclusive), plus `limit` and `after`.
 *
 * **A `sku` the deployment does not meter is a 400**, not an empty page: a SKU is a row this
 * deployment happens to declare, so an unknown one is a typo rather than a meter with no usage.
 */
export type UsageDaysQuery = NonNullable<operations['orgs_usage_days_list']['parameters']['query']>
export type UsagePeriodsQuery = NonNullable<
  operations['orgs_usage_periods_list']['parameters']['query']
>

/** The catalogue's meter names, as the backend's own catalogue spells them. */
export const USAGE_SKU = {
  /** Gigabytes delivered. */
  BANDWIDTH: 'bandwidth.delivered',
  /** Gigabyte-days of stored files. */
  STORAGE: 'storage.daily',
  /** Hour-days of stored video. */
  VIDEO_STORAGE: 'storage.video_daily',
} as const

/**
 * A page of metered days, newest first and no `order` — a usage screen opens on the days just
 * measured. **A day with `charged_at` null has not been settled**: either it is too recent, or the
 * wallet could not pay for it and the pass will take it again, so a gap in `amount_cents` is a
 * backlog rather than a day given away.
 */
export function listUsageDays(
  query: UsageDaysQuery = {},
  signal?: AbortSignal,
): Promise<UsageDayPage> {
  return api.get<UsageDayPage>(withQuery('/orgs/current/usage-days', query), undefined, signal)
}

/**
 * The windows a free allowance is published on, newest first. `included_quantity` is the allowance
 * **frozen when the period opened**, so a tariff changed mid-month does not rewrite what that
 * month was promised; `closed_at` is null while the period is still running.
 */
export function listUsagePeriods(
  query: UsagePeriodsQuery = {},
  signal?: AbortSignal,
): Promise<UsagePeriodEntry[]> {
  return api.get<UsagePeriodEntry[]>(
    withQuery('/orgs/current/usage-periods', query),
    undefined,
    signal,
  )
}

/**
 * Every live monthly fee — one per channel, one per custom host — with its dunning state:
 * `active`, `past_due` (with `grace_ends_on`, the last day before the resource goes offline),
 * `suspended` or `cancelled`. **The server's list, not a sum of prices the SPA knows**: an exempt
 * or grandfathered channel is simply not charged, and nothing here has to know which.
 */
export function listRecurringCharges(signal?: AbortSignal): Promise<RecurringChargeEntry[]> {
  return api.get<RecurringChargeEntry[]>('/orgs/current/recurring-charges', undefined, signal)
}
